import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { Cover } from '../../components/primitives/Cover';
import type { BrowseTop } from '../hooks/useBrowseSearch';
import { formatChange, formatCount, formatReach } from '../browseFormat';
import { browseMessages as messages } from '../browseMessages';
import { BrowseCostChip } from './BrowseCostChip';
import { BrowseFollowButton } from './BrowseFollowButton';
import { BrowseOpenLink } from './BrowseOpenLink';
import { BrowseTrackRow } from './BrowseTrackRow';
import { changeToneClass } from './changeToneClass';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseTopResultProps {
  top: BrowseTop;
  numbersDate: string | null;
}

/** A stat tile: a nested Panel with no head (one box rule). */
const Tile: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => (
  <Panel nested className={styles.tile}>
    <span className={styles.tileKey}>{label}</span>
    <span className={clsx(styles.tileValue, className)}>{value}</span>
  </Panel>
);

/** The Top result panel of the All tab (design a1): the best artist with its top tracks, or a track. */
const BrowseTopResultComponent: React.FC<BrowseTopResultProps> = ({ top, numbersDate }) => {
  const intl = useIntl();
  const item = top.kind === 'artist' ? top.artist : top.track;
  const sub =
    top.kind === 'artist'
      ? top.artist.genres.slice(0, 3).join(' · ')
      : `${top.track.artistName} · ${top.track.albumName}`;
  const date =
    numbersDate === null
      ? null
      : intl.formatDate(`${numbersDate}T12:00:00Z`, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.topResult)}
        icon={<FontAwesomeIcon icon={faSparkles} />}
        right={date === null ? undefined : intl.formatMessage(messages.numbersAsOf, { date })}
      />
      <PanelBody flush>
        <div className={styles.topGrid}>
          <div className={styles.topMain}>
            <div className={styles.topId}>
              <Cover src={item.imageUrl} label={item.name} size={88} radius={top.kind === 'artist' ? '50%' : 12} />
              <div>
                <span className={styles.topKind}>
                  {intl.formatMessage(top.kind === 'artist' ? messages.kindArtist : messages.kindTrack)}
                </span>
                <h2 className={styles.topName}>{item.name}</h2>
                {sub !== '' && <div className={styles.topSub}>{sub}</div>}
              </div>
            </div>
            <div className={styles.tiles}>
              {top.kind === 'artist' && (
                <Tile
                  label={intl.formatMessage(messages.statFollowers)}
                  value={formatCount(intl, top.artist.followers)}
                />
              )}
              <Tile label={intl.formatMessage(messages.statReach)} value={formatReach(intl, item.reach)} />
              <Tile label={intl.formatMessage(messages.statPlaylists)} value={formatCount(intl, item.playlists)} />
              <Tile
                label={intl.formatMessage(messages.stat7d)}
                value={formatChange(intl, item.change7d)}
                className={changeToneClass(item.change7d)}
              />
            </div>
            <div className={styles.topActions}>
              <BrowseFollowButton kind={top.kind} id={item.id} name={item.name} followed={item.state === 'followed'} />
              <BrowseOpenLink kind={top.kind} id={item.id} state={item.state} className={styles.openLink}>
                {intl.formatMessage(top.kind === 'artist' ? messages.openArtist : messages.openTrack)}
                <FontAwesomeIcon icon={faArrowRight} />
              </BrowseOpenLink>
              <BrowseCostChip state={item.state} />
            </div>
          </div>
          {top.kind === 'artist' && top.topTracks.length > 0 && (
            <div className={styles.topSide}>
              <div className={styles.topSideHead}>
                {intl.formatMessage(messages.topTracksBy, { artist: item.name })}
              </div>
              {top.topTracks.map((track, index) => (
                <BrowseTrackRow key={track.id} track={track} rank={index + 1} compact />
              ))}
            </div>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const BrowseTopResult = TypedMemo(BrowseTopResultComponent);
